import { CSSObject, debounce, styled, Theme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  useCreateTransactionMutation,
  useGetConfigurationQuery,
  useGetDashboardOperationsQuery,
  useGetFiatAccountsQuery,
} from '@/api/v1';
import ExpandIcon from '@/assets/icons/dropdown-arrow.svg?react';
import { AddObjectButton } from '@/components/molecules/AddObjectButton';
import { ExpectedSettlementTimeInfo } from '@/components/molecules/ExpectedSettlementTimeInfo';
import { FullScreenModalContainer } from '@/components/molecules/FullScreenModalContainer';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { OutsideBRLMarketHoursAlert } from '@/components/molecules/OutsideBRLMarketHoursAlert';
import { AmountAndCurrencyWidget } from '@/components/organisms/AmountAndCurrencyWidget';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { Header } from '@/components/organisms/Header';
import { TransactionDetails } from '@/components/organisms/TransactionDetails';
import { useBalance } from '@/hooks/useBalance';
import { useCustomer } from '@/hooks/useCustomer';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { useSimulation } from '@/hooks/useSimulation';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { Section } from '@/Screens/Dashboard/Flows/TransferFlow/Section';
import {
  destinationChanged,
  fromAmountChanged,
  fromCurrencyChanged,
  initialState,
  transactionFlowChanged,
  WithdrawalFlowAction,
  withdrawalFlowReducer,
  WithdrawalFlowState,
} from '@/Screens/Dashboard/Flows/WithdrawalFlow/reducer';
import { DetailedBalance } from '@/types/bankInfo';
import { FiatAccount } from '@/types/contacts';
import { isPIXAccountDetails } from '@/types/contacts.ext';
import {
  CurrencyCodeType,
  DashboardOperation,
  DashboardOperationDestination,
} from '@/types/currency';
import { Page } from '@/types/page';
import { MarketExecutionType } from '@/types/transaction';
import { isBRLOperationOutsideMarketHours } from '@/utility/brlMarketHours';
import { AsAnyActionArg } from '@/utility/reducers';

enum FormErrors {
  MISSING_ACCOUNT = 'MISSING_ACCOUNT',
  MISSING_AMOUNT = 'MISSING_AMOUNT',
  AMOUNT_BIGGER_THAN_BALANCE = 'AMOUNT_BIGGER_THAN_BALANCE',
}

export const WithdrawalFlow: React.FC = (): React.ReactElement => {
  const customer = useCustomer();
  const usdcBalance = useBalance(CurrencyCodeType.USDC);
  const usdtBalance = useBalance(CurrencyCodeType.USDT);

  const toast = useToast();
  const tr = useTranslator();
  const location = useLocation();
  const [error, setError] = React.useState<FormErrors | null>(null);

  const { data: operations = [], isFetching: fetchingTransactionFlows } =
    useGetDashboardOperationsQuery('WITHDRAWAL');

  const { data: accountsPage = Page.empty<FiatAccount>(), isFetching: fetchingAccounts } =
    useGetFiatAccountsQuery(customer.beneficiaryId, {
      skip: !customer.beneficiaryId,
    });

  const reducer = React.useReducer<WithdrawalFlowState, AsAnyActionArg<WithdrawalFlowAction>>(
    withdrawalFlowReducer,
    initialState,
  );

  const [createTransaction, mutation] = useCreateTransactionMutation();
  const [state, dispatch] = reducer;
  const { destination, fromAmount, fromCurrency, toAmount, toCurrency, paymentRail } = state;

  const [simulation, fetchingSimulation] = useSimulation({
    fromAmount: fromAmount,
    toAmount: null,
    simulationId: null,
    fromCurrency: fromCurrency,
    toCurrency: toCurrency,
    fundingMethod: null,
    paymentRail: paymentRail,
    destination: destination,
  });

  const balances = React.useMemo(
    (): Partial<Record<CurrencyCodeType, DetailedBalance>> => ({
      [CurrencyCodeType.USDC]: usdcBalance,
      [CurrencyCodeType.USDT]: usdtBalance,
    }),
    [usdcBalance, usdtBalance],
  );

  const recentlyAddedAccountId = React.useMemo((): string | null => {
    const { state } = location;
    return state?.accountId ?? null;
  }, [location]);

  const currentBalance = React.useMemo(
    (): DetailedBalance => balances[fromCurrency] ?? DetailedBalance.zero(fromCurrency),
    [balances, fromCurrency],
  );

  React.useEffect((): VoidFunction | void => {
    if (operations.length > 0) {
      dispatch(transactionFlowChanged(operations[0]));
    }
  }, [dispatch, operations]);

  const navigate = useNavigate();
  const onClose = React.useCallback((): void => {
    navigate('/home');
  }, [navigate]);

  const accounts = React.useMemo((): readonly FiatAccount[] => {
    const { content } = accountsPage;
    return content.filter((account: FiatAccount): boolean => {
      if (!account.belongsToBeneficiary) {
        return false;
      }

      if (isPIXAccountDetails(account.accountDetails)) {
        return toCurrency === CurrencyCodeType.BRL;
      } else {
        return toCurrency === CurrencyCodeType.USD;
      }
    });
  }, [accountsPage, toCurrency]);

  const destinationRenderer = React.useCallback((account: FiatAccount): React.ReactNode => {
    return (
      <MenuItem key={account.id} value={account.id}>
        {account.name}
      </MenuItem>
    );
  }, []);

  const currencies = React.useMemo((): readonly CurrencyCodeType[] => {
    return operations
      .filter((operation: DashboardOperation): boolean =>
        operation.destinations.some(
          (destination: DashboardOperationDestination): boolean =>
            destination.currency === toCurrency,
        ),
      )
      .map((operation: DashboardOperation): CurrencyCodeType => operation.currency);
  }, [toCurrency, operations]);

  const loading = React.useMemo(
    (): boolean =>
      fetchingAccounts ||
      fetchingSimulation ||
      fetchingTransactionFlows ||
      mutation.status === QueryStatus.pending,
    [fetchingAccounts, fetchingSimulation, fetchingTransactionFlows, mutation.status],
  );

  const accountLabel = React.useMemo((): React.ReactNode => {
    if (fetchingAccounts) {
      return <Box sx={styles.loadingText} />;
    } else if (accounts.length === 0) {
      return tr('NO_ACCOUNTS');
    } else {
      return tr('SELECT_ACCOUNT');
    }
  }, [accounts.length, fetchingAccounts, tr]);

  const onAccountChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (error === FormErrors.MISSING_ACCOUNT) {
        setError(null);
      }

      const account = accounts.find(
        (account: FiatAccount): boolean => account.id === event.target.value,
      );
      if (account) {
        dispatch(destinationChanged(account));
      }
    },
    [dispatch, accounts, error, setError],
  );

  const ready = React.useMemo((): boolean => destination !== '', [destination]);
  const onFromAmountChange = React.useCallback(
    (value: number | null): void => {
      if (error === FormErrors.AMOUNT_BIGGER_THAN_BALANCE || error === FormErrors.MISSING_AMOUNT) {
        setError(null);
      }

      dispatch(fromAmountChanged(value));
    },
    [error, setError, dispatch],
  );

  const onConfirmWithdrawal = React.useCallback((): void => {
    if (!fromAmount) {
      return setError(FormErrors.MISSING_AMOUNT);
    }
    if (fromAmount > usdcBalance.available) {
      return setError(FormErrors.AMOUNT_BIGGER_THAN_BALANCE);
    }

    if (simulation.id) {
      createTransaction({
        simulationId: simulation.id,
        beneficiaryIp: '0.0.0.0',
        marketExecutionType: MarketExecutionType.executeImmediately,
      });
    }
  }, [fromAmount, usdcBalance, setError, createTransaction, simulation.id]);

  const computedToAmount = React.useMemo((): number | null => {
    if (simulation.id === null) {
      return toAmount;
    } else {
      return simulation.to.value;
    }
  }, [simulation.id, simulation.to.value, toAmount]);

  const onSetMaxAmount = React.useCallback((): void => {
    onFromAmountChange(currentBalance.available);
  }, [onFromAmountChange, currentBalance.available]);

  const formatter = useLocalizedFormatter({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formattedBalance = React.useMemo((): string => {
    return formatter.format(currentBalance.available);
  }, [formatter, currentBalance]);

  const onFromCurrencyChange = React.useCallback(
    (currency: CurrencyCodeType): void => {
      dispatch(fromCurrencyChanged(currency));
      dispatch(fromAmountChanged(null));
    },
    [dispatch],
  );

  React.useEffect((): void => {
    if (mutation.status === QueryStatus.fulfilled) {
      navigate('/home/withdraw/completed');
    }
    if (mutation.status === QueryStatus.rejected) {
      toast.show(tr('AN_ERROR_OCCURRED'), 'error');
    }
  }, [mutation.status, toast, navigate, tr]);

  React.useEffect((): void => {
    if (recentlyAddedAccountId) {
      const account = accounts.find(
        (account: FiatAccount): boolean => account.id === recentlyAddedAccountId,
      );

      if (account) {
        dispatch(destinationChanged(account));
      }
    }
  }, [accounts, dispatch, recentlyAddedAccountId, onAccountChange]);

  const amountHelperText = React.useMemo(
    (): string | null =>
      match(error)
        .with(FormErrors.MISSING_AMOUNT, (): string => tr('REQUIRED_AMOUNT'))
        .with(FormErrors.AMOUNT_BIGGER_THAN_BALANCE, (): string =>
          tr('AMOUNT_BIGGER_THAN_BALANCE', formattedBalance),
        )
        .otherwise((): null => null),
    [error, formattedBalance, tr],
  );

  const {
    data: configuration,
    isFetching: isFetchingConfiguration,
    isError: isErrorConfiguration,
  } = useGetConfigurationQuery();

  const shouldNotAllowOperation = React.useMemo((): boolean => {
    if (isErrorConfiguration || !configuration?.brazilianMarketHours) {
      return false;
    }
    return (
      isFetchingConfiguration ||
      isBRLOperationOutsideMarketHours(fromCurrency, configuration.brazilianMarketHours)
    );
  }, [
    isErrorConfiguration,
    configuration?.brazilianMarketHours,
    isFetchingConfiguration,
    fromCurrency,
  ]);

  return (
    <ApplicationDialog fullScreen={true}>
      <Header title={tr('WITHDRAW_FUNDS')} onClose={onClose} />
      <FullScreenModalContainer>
        <Section
          title={tr('WITHDRAWAL_ACCOUNT')}
          visible={true}
          working={mutation.status === QueryStatus.pending}
          spotlight={true}>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label={accountLabel}
              select={true}
              fullWidth={true}
              disabled={loading}
              value={destination}
              onChange={onAccountChange}
              error={error === FormErrors.MISSING_ACCOUNT}
              helperText={error === FormErrors.MISSING_ACCOUNT ? tr('REQUIRED_ACCOUNT') : null}>
              {accounts.map(destinationRenderer)}
            </TextField>
            <Box py={3}>
              <AddObjectButton label={tr('ADD_NEW_WITHDRAWAL_ACCOUNT')} path="account" />
            </Box>
          </Box>
        </Section>
        <Section
          title={tr('AMOUNT')}
          visible={destination !== ''}
          working={loading}
          spotlight={true}>
          <Box display="flex" flexDirection="column" gap={2}>
            <AmountAndCurrencyWidget
              label={tr('YOU_WITHDRAW')}
              currencies={currencies}
              amount={fromAmount}
              currency={fromCurrency}
              disabled={!ready}
              helperText={amountHelperText}
              error={!!amountHelperText}
              onAmountChange={debounce(onFromAmountChange, 300)}
              onCurrencyChange={onFromCurrencyChange}
            />
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" color="custom.LightGray">
                {tr('WALLET_BALANCE', formattedBalance)}
              </Typography>
              <Button
                variant="dark"
                size="small"
                sx={styles.maxButton}
                disabled={!ready}
                onClick={onSetMaxAmount}>
                {tr('MAX')}
              </Button>
            </Box>
            <AmountAndCurrencyWidget
              label={tr('YOU_RECEIVE')}
              currencies={[toCurrency]}
              amount={computedToAmount}
              currency={toCurrency}
              disabled={!ready}
              readOnly={true}
            />
            {shouldNotAllowOperation && <OutsideBRLMarketHoursAlert />}
            <StyledAccordion disabled={simulation.id === null}>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <Typography variant="h7">{tr('TRANSACTION_DETAILS')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TransactionDetails
                  transactionDetails={simulation.transactionDetails}
                  fromAmount={fromAmount}
                  fromCurrency={fromCurrency}
                  toAmount={simulation.to.value}
                  toCurrency={toCurrency}
                />
              </AccordionDetails>
            </StyledAccordion>

            <Box sx={styles.expectedSettlementTimeInfo}>
              <ExpectedSettlementTimeInfo
                expectedSettlementTimeDetails={
                  simulation.transactionDetails.expectedSettlementTimeDetails
                }
              />
            </Box>
          </Box>
        </Section>
        <Box width={552} mx="auto">
          <LoadingButton
            variant="contained"
            color="primary"
            fullWidth={true}
            loading={loading}
            disabled={simulation.id === null || shouldNotAllowOperation}
            onClick={onConfirmWithdrawal}>
            {tr('CONFIRM_WITHDRAWAL')}
          </LoadingButton>
        </Box>
      </FullScreenModalContainer>

      <Outlet />
    </ApplicationDialog>
  );
};

const StyledAccordion = styled(Accordion)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    border: 'none',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.custom.DividerLight,
    borderRadius: 16,
    '&::before': {
      display: 'none',
    },
    '&:last-of-type': {
      borderRadius: 16,
    },
  }),
);

const styles: Record<string, SystemStyleObject> = {
  maxButton: {
    padding: 0,
    width: 53,
    minWidth: 0,
  },
  loadingText: {
    my: 0.75,
    height: 16,
    width: 240,
    animation: 'skeleton 1s infinite',
    backgroundColor: 'custom.Skeleton',
    borderRadius: 2,
  },
  expectedSettlementTimeInfo: {
    display: 'flex',
    justifyContent: 'center',
    mt: 3,
    color: 'primary.main',
  },
};
